import { AUDIO_CONSTANTS } from '../../config/audio-constants.js';
import type { AudioSetupOptions, ChannelDataSource, PlaybackMode } from '../../types/seamless.js';
import type { AudioTrackLength } from '../../types/audio-length.js';
import { getSyncLength, getPlaybackLength } from '../../utils/audio-length-utils.js';
import { frameBufferCache } from '../../../audio-engine/memory/frame-buffer-cache.js';

/**
 * Seamless setup helper for continuous looping without buffer resets
 */
export async function setupSeamlessPlayback(
  options: AudioSetupOptions,
  channelDataSource: ChannelDataSource,
  playbackWorklet: AudioWorkletNode,
  stretchWorklet: AudioWorkletNode | null,
  trackLength: AudioTrackLength
): Promise<void> {
  const mode: PlaybackMode = stretchWorklet ? 'tempo-synced' : 'basic';

  // Use clear terminology for sync and reading boundaries
  const syncBoundary = getSyncLength(trackLength);
  const readBoundary = getPlaybackLength(trackLength);
  const encoderDelay = trackLength.metadata.encoderDelay;

  console.log('🎵 Setting up seamless playback:', {
    mode,
    syncBoundary,
    readBoundary,
    encoderDelay
  });

  // Track reading position
  let currentReadPosition = 0;
  let isReading = false;

  // Buffer management
  const FRAMES_PER_READ = AUDIO_CONSTANTS.FRAMES_PER_READ;
  const FRAME_SIZE = AUDIO_CONSTANTS.FRAME_SIZE;

  // Cleanup stale buffers periodically
  const cleanupInterval = setInterval(() => {
    frameBufferCache.cleanup();
  }, 2000);

  // Continuous reading function
  const continuousRead = async () => {
    if (isReading) {
      return;
    }
    isReading = true;

    try {
      while (true) {
        const framesToSend = [];
        const frameIds: string[] = [];

        for (let i = 0; i < FRAMES_PER_READ; i++) {
          const start = currentReadPosition;
          const end = Math.min(start + FRAME_SIZE, readBoundary);

          // Read the frame
          const channelData = await channelDataSource.readChannelData(start, end);

          if (channelData && channelData.length > 0 && channelData[0].length > 0) {
            framesToSend.push(channelData);

            // Note: We can't use frame buffer cache directly here because
            // readChannelData already uses the pool internally
            // In a full implementation, we'd refactor to use frameBufferCache throughout
          }

          // Update position with wrapping
          currentReadPosition += FRAME_SIZE;

          // Wrap using clear terminology for reading boundaries
          if (readBoundary && readBoundary > 0) {
            // Use readBoundary for buffer reading limits
            if (currentReadPosition >= readBoundary) {
              currentReadPosition = 0; // Wrap to start of audio data
              console.log('📍 Read position wrapped from read boundary to 0 (clear behavior)');
            }
          }
        }

        // Send frames to worklet
        if (framesToSend.length > 0) {
          // Always send frames to playback worklet
          // The stretch worklet processes the audio output, not raw frames
          playbackWorklet.port.postMessage(framesToSend);
        }

        // Small delay to prevent overwhelming the system
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    } catch (error) {
      console.error('Error in continuous read:', error);
      isReading = false;
    } finally {
      // Clear the cleanup interval when reading stops
      clearInterval(cleanupInterval);
    }
  };

  // Send track length to worklet with clear terminology
  playbackWorklet.port.postMessage({
    type: 'setTrackLength',
    trackLength: {
      syncLength: syncBoundary,
      playbackLength: readBoundary,
      metadata: {
        encoderDelay: trackLength.metadata.encoderDelay,
        endPadding: trackLength.metadata.endPadding
      }
    }
  });

  // Handle worklet messages
  playbackWorklet.port.onmessage = async event => {
    const data = event.data;

    if (data === 'ready') {
      console.log('✅ Playback worklet ready');
      // Start reading when worklet is ready
      if (!isReading) {
        continuousRead();
      }
      return;
    }

    if (data?.prefetch) {
      // Handle prefetch requests for loop start
      console.log('📥 Prefetch request for loop start:', data.prefetch.length, 'frames');

      const prefetchFrames = [];
      for (const request of data.prefetch) {
        const start = request.position;
        const end = Math.min(start + FRAME_SIZE, readBoundary);

        const channelData = await channelDataSource.readChannelData(start, end);
        if (channelData && channelData.length > 0) {
          // Mark as loop start frame
          prefetchFrames.push({
            ...channelData,
            isLoopStart: true
          });
        }
      }

      if (prefetchFrames.length > 0) {
        playbackWorklet.port.postMessage(prefetchFrames);
      }
      return;
    }

    if (Array.isArray(data)) {
      // Regular frame requests - update read position
      const firstRequest = data[0];
      if (typeof firstRequest === 'number') {
        // Jump read position if significantly different
        if (Math.abs(firstRequest - currentReadPosition) > FRAME_SIZE * 20) {
          console.log('🎯 Jumping read position from', currentReadPosition, 'to', firstRequest);
          currentReadPosition = firstRequest;
        }
      }
    }

    // Start continuous reading if not already running
    if (!isReading) {
      continuousRead();
    }
  };

  // No need to forward messages between worklets
  // The audio flows through the Web Audio API connections

  // Start continuous reading
  console.log('🚀 Starting continuous seamless reading...');
  continuousRead().catch(error => {
    console.error('❌ Error starting continuous read:', error);
  });
}
